import { useEffect, useMemo, useRef, useState } from 'react';

import { useHydration } from '../../Search/hooks/useHydration';
import { DEFAULT_ASPECT_RATIO } from '../constants';
import {
  JustifiedGalleryRow,
  JustifiedGalleryLayoutData,
  UseJustifiedGalleryLayoutOptions,
  JustifiedGalleryItem,
  JustifiedGalleryItemGeneric,
} from './types';
import { getCurrentHeight, getTargetAspectSumForRow, toFixed } from './utils';

export function useJustifiedGalleryLayout<T = JustifiedGalleryItem>({
  items,
  responsiveHeights,
  lastRowStretchThreshold,
  rowGapPx = 8,
  columnGapPx = 8,
}: UseJustifiedGalleryLayoutOptions<T>): JustifiedGalleryLayoutData<T> {
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHydrated = useHydration();

  // Update container width on resize using ResizeObserver
  useEffect(() => {
    if (!isHydrated || !containerRef.current) return;
    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width } = entry.contentRect;
      setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [isHydrated]);

  // Calculate row information to determine last row behavior
  const itemsLength = items.length;
  const itemsAspectRatios = items
    .map((item) => item.aspectRatio || DEFAULT_ASPECT_RATIO)
    .join(',');

  // Get the current heights based on container width
  const targetRowHeight = getCurrentHeight(responsiveHeights, containerWidth);

  const { rows, lastRowIndex } = useMemo(() => {
    if (!itemsLength) return { rows: [], lastRowIndex: -1 };

    const rows: JustifiedGalleryRow<T>[] = [];
    let currentRow: JustifiedGalleryItemGeneric<T>[] = [];
    let currentAspectSum = 0;

    for (const item of items) {
      const safeAspectRatio = toFixed(item.aspectRatio || DEFAULT_ASPECT_RATIO);

      const targetAspectSum = getTargetAspectSumForRow(
        currentRow.length,
        targetRowHeight,
        columnGapPx,
        containerWidth
      );

      // Calculate proposed aspect sum:
      // - If currentRow is empty, this is the first item (no gap before it)
      // - Otherwise, add the gap before this item (converted to aspect ratio equivalent)
      const gapAspectRatio =
        currentRow.length > 0 ? toFixed(columnGapPx / targetRowHeight) : 0;

      const proposedAspectSum =
        currentAspectSum + safeAspectRatio + gapAspectRatio;

      if (proposedAspectSum >= targetAspectSum && currentRow.length > 0) {
        rows.push({
          items: currentRow,
          aspectSum: currentAspectSum,
          isLastRow: false,
        });
        currentRow = [item];
        currentAspectSum = safeAspectRatio;
      } else {
        currentRow.push(item);
        currentAspectSum = proposedAspectSum;
      }
    }

    if (currentRow.length > 0) {
      rows.push({
        items: currentRow,
        aspectSum: currentAspectSum,
        isLastRow: true,
      });
    }

    return {
      rows,
      lastRowIndex: rows.length - 1,
    };
  }, [
    itemsLength,
    itemsAspectRatios,
    targetRowHeight,
    containerWidth,
    rowGapPx,
    columnGapPx,
  ]);

  // Determine if last row should stretch
  const shouldLastRowStretch = useMemo(() => {
    if (lastRowIndex < 0) return false;

    const lastRow = rows[lastRowIndex];
    const targetAspectSum = getTargetAspectSumForRow(
      lastRow.items.length,
      targetRowHeight,
      columnGapPx,
      containerWidth
    );

    // Check if last row's aspect sum is less than the threshold percentage of target
    const thresholdAspectSum = toFixed(
      targetAspectSum * lastRowStretchThreshold
    );
    return lastRow.aspectSum >= thresholdAspectSum;
  }, [
    lastRowIndex,
    targetRowHeight,
    lastRowStretchThreshold,
    containerWidth,
    rows.length,
  ]);

  return {
    rows,
    lastRowIndex,
    shouldLastRowStretch,
    containerWidth,
    isHydrated,
    containerRef,
  };
}
